interface GoogleLatLng {
  lat: number
  lng: number
}

interface GoogleMapOptions {
  center: GoogleLatLng
  zoom: number
  styles: unknown[]
  mapTypeId: string
  mapTypeControl: boolean
}

interface GoogleMap {
  setCenter: (latlng: GoogleLatLng) => void
}

interface GoogleMarkerOptions {
  position: GoogleLatLng
  map: GoogleMap
  icon?: string
}

interface GoogleGeometry {
  location: GoogleLatLng
}

interface GoogleGeocoderResult {
  geometry: GoogleGeometry
  formatted_address: string
}

interface GoogleGeocoder {
  geocode: (
    request: { address: string },
    callback: (results: GoogleGeocoderResult[] | null, status: string) => void
  ) => void
}

declare global {
  interface Window {
    google?: {
      maps: {
        Map: new (element: HTMLElement, options: GoogleMapOptions) => GoogleMap
        Marker: new (options: GoogleMarkerOptions) => void
        Geocoder: new () => GoogleGeocoder
      }
    }
  }
}

interface MapConfig {
  marker: string | null
  coordinates: GoogleLatLng | null
  address: string | null
  maptype: string
  zoom: number
  styles: unknown[]
  formatted_address?: string
}

const hydratedGoogleMaps = new WeakSet<HTMLElement>()

export function hydrateGoogleMapStripes(
  container: Document | Element = document
): void {
  const googleMaps = container.querySelectorAll<HTMLElement>(
    '.vssl-stripe--googlemap'
  )

  googleMaps.forEach((stripeEl) => {
    if (hydratedGoogleMaps.has(stripeEl)) return
    hydratedGoogleMaps.add(stripeEl)

    // Check if Google Maps API is available
    if (typeof window.google === 'undefined') {
      console.error('Google Maps API is not available in this script.')
      return
    }

    try {
      initStripe(stripeEl)
    } catch (e) {
      console.error('Failed to initialize google map stripe', e)
    }
  })
}

function initStripe(stripeEl: HTMLElement): void {
  // Read configuration from data attributes
  const markerAttr = stripeEl.dataset.marker || null
  const coordinatesAttr = stripeEl.dataset.coordinates || null
  const addressAttr = stripeEl.dataset.address || null
  const maptypeAttr = stripeEl.dataset.maptype || 'roadmap'
  const zoomAttr = parseInt(stripeEl.dataset.zoom || '14', 10)
  const stylesAttr = stripeEl.dataset.styles || '[]'

  // Parse coordinates if provided
  let coordinates: GoogleLatLng | null = null
  if (coordinatesAttr) {
    try {
      coordinates = JSON.parse(coordinatesAttr) as GoogleLatLng
    } catch (e) {
      console.error('Failed to parse coordinates', e)
    }
  }

  // Parse styles (only for roadmap type)
  const styles = maptypeAttr === 'roadmap' ? JSON.parse(stylesAttr) : []

  const config: MapConfig = {
    marker: markerAttr,
    coordinates,
    address: addressAttr,
    maptype: maptypeAttr,
    zoom: zoomAttr,
    styles
  }

  if (coordinates) {
    buildMap(stripeEl, config)
  } else if (addressAttr) {
    geocode(stripeEl, config)
  } else {
    console.info(
      'No coordinates or address set for Google Maps API. Doing nothing.'
    )
  }

  // Add embed element click handler
  const embedEl = stripeEl.querySelector<HTMLElement>(
    '.vssl-stripe--googlemap--embed'
  )
  if (embedEl) {
    // eslint-disable-next-line space-before-function-paren
    embedEl.addEventListener('click', function () {
      this.classList.add('is-engaged')
    })
  }
}

function buildMap(stripeEl: HTMLElement, config: MapConfig): void {
  if (!window.google || !config.coordinates) return

  const mapWrapEl = stripeEl.querySelector<HTMLElement>(
    '.vssl-stripe--googlemap--map'
  )
  if (!mapWrapEl) return

  const customMarker = 'https://s3.amazonaws.com/cdn.vssl.io/marker.png'

  const map = new window.google.maps.Map(mapWrapEl, {
    center: config.coordinates,
    zoom: config.zoom,
    styles: config.styles,
    mapTypeId: config.maptype,
    mapTypeControl: false
  })

  // eslint-disable-next-line no-new
  new window.google.maps.Marker({
    map,
    position: config.coordinates,
    icon: config.marker || customMarker
  })
}

function geocode(stripeEl: HTMLElement, config: MapConfig): void {
  if (!window.google || !config.address) return

  const geocoder = new window.google.maps.Geocoder()

  geocoder.geocode({ address: config.address }, (results, status) => {
    if (status === 'OK' && results && results.length > 0) {
      config.coordinates = results[0].geometry.location
      config.formatted_address = results[0].formatted_address
      buildMap(stripeEl, config)
    }
  })
}
